import styled from '@emotion/styled';
import {
  BrowserMessageType,
  sendElectronMessage,
} from 'electron-communication-lib/BrowserForApp';
import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {
  EDynamicTooltip,
  EIconSmallButton,
  EListEntryComponentProps,
} from 'webapp-lib/Components';
import {
  mixinFlexboxColumn,
  mixinFlexboxRow,
  mixinSVGIconMedium,
  mixinSVGIconSmall,
  mixinTruncate,
} from 'webapp-lib/styling';

import {Delete, Warning} from '_/assets/images';
import {DOCUMENT_ICONS_MAP} from '_/constants';

import {FileInformation} from './FileShelf.types';

export const FileListComponent = ({
  item,
  onClick,
  onDoubleClick,
  onContextMenu,
}: EListEntryComponentProps<FileInformation>): React.ReactElement => {
  const {t} = useTranslation('documents');
  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onClick(event);
    },
    [onClick]
  );

  const deleteFile = () =>
    sendElectronMessage({
      payload: [item.fileId],
      type: BrowserMessageType.FILE_SHELF_DELETE_FILES,
    });

  return (
    <StyledShelfItem
      role='button'
      tabIndex={0}
      onClick={handleClick}
      onContextMenu={onContextMenu}
      onDoubleClick={onDoubleClick}
    >
      <StartAdornmentIcon>{DOCUMENT_ICONS_MAP[item.type]}</StartAdornmentIcon>
      <RowsWrapper>
        <UpperLine>
          <EDynamicTooltip title={item.originalFileName}>
            <TextOnly>
              {item.casusDisplayNameWithClient ?? t('fileList.noCasus')}
            </TextOnly>
          </EDynamicTooltip>
          <IconAdornment onClick={deleteFile}>
            <Delete />
          </IconAdornment>
        </UpperLine>
        <EDynamicTooltip title={item.originalFileName}>
          <TextOnly>{item.originalFileName}</TextOnly>
        </EDynamicTooltip>
        <LowerLine>
          <span>
            {item.type === 'EMAIL'
              ? t('communication:fileshelf.sent')
              : t('fileshelf.documentNr')}
            :
          </span>
          <EDynamicTooltip title={item.additionalInformation}>
            <TextOnly>{item.additionalInformation}</TextOnly>
          </EDynamicTooltip>
        </LowerLine>
        <StatusElement item={item} />
      </RowsWrapper>
    </StyledShelfItem>
  );
};

const StatusElement = ({
  item,
}: {
  item: FileInformation;
}): React.ReactElement | null => {
  const {t} = useTranslation('documents');

  if (item.state === 'IN_PROGRESS' || item.state === 'INITIALZING') {
    return (
      <Progressbar>
        <ProgressbarSlider style={{width: `${item.progress} %`}} />
      </Progressbar>
    );
  }
  if (item.state === 'FAILED') {
    return (
      <LowerLine>
        <Warning />
        {t('fileList.failedDownload')}
      </LowerLine>
    );
  }
  return null;
};

const UpperLine = styled.div`
  ${mixinFlexboxRow}

  font-weight: var(--font-weight-title);
`;

const LowerLine = styled.div`
  ${mixinFlexboxRow}
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  gap: var(--gutter-tiny);
  svg {
    ${mixinSVGIconSmall}
  }
`;

const TextOnly = styled.div`
  color: var(--color-core-black);
  ${mixinTruncate}
`;

const StyledShelfItem = styled.div`
  padding: var(--gutter-small);

  ${mixinFlexboxRow}
  gap: var(--gutter);
  font-size: var(--font-size-small);
  user-select: none;
  justify-content: flex-start;
  width: 100%;
  border-bottom: var(--regular-border-width) solid var(--color-core-grey);
  cursor: pointer;
  align-items: center;
  color: var(--color-core-black);
`;

const IconAdornment = styled(EIconSmallButton)`
  flex-grow: 0;
  flex-shrink: 0;
  margin-right: var(--gutter-tiny);
  padding: 0;

  & .WrapperEIconButton {
    height: fit-content;
  }
`;

const StartAdornmentIcon = styled.div`
  ${mixinSVGIconMedium}
  align-items: center;
`;

const RowsWrapper = styled.div`
  ${mixinFlexboxColumn}
  gap: var(--gutter-tiny);
  flex-grow: 1;
  overflow: hidden;
`;

const Progressbar = styled.div`
  width: 90%;
  background-color: var(--color-core-grey-light);
  border-radius: 4px;
  position: relative;
  height: 8px;
  margin-top: var(--gutter-tiny);
`;

const ProgressbarSlider = styled.div`
  background-color: var(--color-core-primary);
  border-radius: 4px;
  position: absolute;
  height: 8px;
  transition: width 1s ease-in-out;
`;
